import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { getMapboxToken } from '../../lib/config';
import { findFeaturedPipeline } from '../../lib/parcel-analysis';
import type { ParcelDetail, NearbyPipeline, BriefingMapLayers, NearestFeature, OperatorCarveOut } from '../../lib/api';

interface SchematicMapProps {
  parcel: ParcelDetail;
  pipelines: NearbyPipeline[];
  mapLayers: BriefingMapLayers;
  nearestFeatures?: {
    gasPlant: NearestFeature | null;
    railroad: NearestFeature | null;
    substation: NearestFeature | null;
    secondSubstation: NearestFeature | null;
    airport: NearestFeature | null;
  };
  // When this parcel sits inside an operator carve-out (e.g. Paces Creek
  // Woodlands' 4-parcel assembly), pass the carve-out so the schematic can
  // overlay the dissolved multi-parcel boundary as a dashed accent polygon.
  carveOut?: OperatorCarveOut | null;
  // Carve-out slug for the featured-pipeline pick (see findFeaturedPipeline). Read from
  // carveOutAggregate, not `carveOut` above, which is suppressed for single-parcel carve-outs.
  carveOutSlug?: string | null;
}

interface PinDef {
  key: string;
  mark: string;
  lng: number;
  lat: number;
  className: string;
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

function nearestVertexInFC(fc: GeoJSON.FeatureCollection | undefined, lng: number, lat: number): [number, number] | null {
  if (!fc?.features?.length) return null;
  let best: [number, number] | null = null;
  let bestDist = Infinity;
  for (const f of fc.features) {
    const g = f.geometry;
    if (!g) continue;
    let verts: number[][] = [];
    if (g.type === 'Point') verts = [g.coordinates];
    else if (g.type === 'LineString') verts = g.coordinates;
    else if (g.type === 'Polygon') verts = g.coordinates[0];
    else if (g.type === 'MultiLineString') verts = g.coordinates.flat();
    else if (g.type === 'MultiPolygon') verts = g.coordinates.flat(2);
    for (const v of verts) {
      const d = (v[0] - lng) ** 2 + (v[1] - lat) ** 2;
      if (d < bestDist) { bestDist = d; best = [v[0], v[1]]; }
    }
  }
  return best;
}

// Centroid of polygon features near a reference point (used to place the fiber pin
// in the middle of the hex coverage rather than on the parcel centroid).
function nearbyPolygonCentroid(fc: GeoJSON.FeatureCollection | undefined, nearLng: number, nearLat: number, withinDeg = 0.03): [number, number] | null {
  if (!fc?.features?.length) return null;
  let sumLng = 0, sumLat = 0, n = 0;
  for (const f of fc.features) {
    const g = f.geometry;
    if (!g) continue;
    let ring: number[][] = [];
    if (g.type === 'Polygon') ring = g.coordinates[0];
    else if (g.type === 'MultiPolygon') ring = g.coordinates[0]?.[0] || [];
    if (ring.length < 3) continue;
    let cLng = 0, cLat = 0;
    for (const [lng, lat] of ring) { cLng += lng; cLat += lat; }
    cLng /= ring.length; cLat /= ring.length;
    if (Math.abs(cLng - nearLng) < withinDeg && Math.abs(cLat - nearLat) < withinDeg) {
      sumLng += cLng; sumLat += cLat; n++;
    }
  }
  return n > 0 ? [sumLng / n, sumLat / n] : null;
}

// Find a point on any railroad line feature that is west of the reference point
// and still near the same corridor (close to the reference latitude). The FRA rail
// dataset splits a single corridor across many short LineString features, so we
// search across ALL vertices, not just the single nearest ring.
function westAlongNearestLine(fc: GeoJSON.FeatureCollection | undefined, refLng: number, refLat: number, minWestDeg = 0.05, maxWestDeg = 0.10, maxLatDeviationDeg = 0.015): [number, number] | null {
  if (!fc?.features?.length) return null;
  let best: [number, number] | null = null;
  let bestScore = Infinity;
  for (const f of fc.features) {
    const g = f.geometry;
    if (!g) continue;
    const rings: number[][][] = g.type === 'LineString' ? [g.coordinates as number[][]]
      : g.type === 'MultiLineString' ? (g.coordinates as number[][][])
      : [];
    for (const ring of rings) {
      for (const [lng, lat] of ring) {
        const west = refLng - lng;
        if (west < minWestDeg || west > maxWestDeg) continue;
        if (Math.abs(lat - refLat) > maxLatDeviationDeg) continue;
        // Score = latitude deviation (stay on corridor) weighted heavier than west span.
        const score = Math.abs(lat - refLat) * 10 - west;
        if (score < bestScore) { bestScore = score; best = [lng, lat]; }
      }
    }
  }
  if (best) return best;
  // Fallback: relax latitude constraint, pick any west-enough vertex closest to ref-lat.
  for (const f of fc.features) {
    const g = f.geometry;
    if (!g) continue;
    const rings: number[][][] = g.type === 'LineString' ? [g.coordinates as number[][]]
      : g.type === 'MultiLineString' ? (g.coordinates as number[][][])
      : [];
    for (const ring of rings) {
      for (const [lng, lat] of ring) {
        const west = refLng - lng;
        if (west < minWestDeg) continue;
        const score = Math.abs(lat - refLat);
        if (score < bestScore) { bestScore = score; best = [lng, lat]; }
      }
    }
  }
  return best;
}

// Nearest point feature to a reference point (used for picking the on-corridor compressor).
function nearestPointFeature(fc: GeoJSON.FeatureCollection | undefined, lng: number, lat: number): { lng: number; lat: number; props: Record<string, any>; miles: number } | null {
  if (!fc?.features?.length) return null;
  let best: { lng: number; lat: number; props: Record<string, any>; miles: number } | null = null;
  let bestDist2 = Infinity;
  for (const f of fc.features) {
    if (f.geometry?.type !== 'Point') continue;
    const [fLng, fLat] = f.geometry.coordinates;
    const d2 = (fLng - lng) ** 2 + (fLat - lat) ** 2;
    if (d2 < bestDist2) {
      bestDist2 = d2;
      // Great-circle approximation: 69 mi/deg lat, cos(lat)*69 mi/deg lng
      const dLat = (fLat - lat) * 69;
      const dLng = (fLng - lng) * Math.cos(lat * Math.PI / 180) * 69;
      best = { lng: fLng, lat: fLat, props: f.properties || {}, miles: Math.sqrt(dLat * dLat + dLng * dLng) };
    }
  }
  return best;
}

function buildPins(
  parcel: ParcelDetail,
  pipelines: NearbyPipeline[],
  nearestFeatures: SchematicMapProps['nearestFeatures'],
  carrierRoutes?: GeoJSON.FeatureCollection,
  fiberCoverage?: GeoJSON.FeatureCollection,
  compressors?: GeoJSON.FeatureCollection,
  railroads?: GeoJSON.FeatureCollection,
  highways?: GeoJSON.FeatureCollection,
  carveOutSlug?: string | null,
): PinDef[] {
  const pins: PinDef[] = [];
  const centroidLng = Number(parcel.lng);
  const centroidLat = Number(parcel.lat);

  // A — featured pipeline tap (DSW first, then best-by-MW, then nearest).
  // A′ / A″ — up to two additional-operator redundant pipelines (different operator
  // from hero), so the reader can identify every drawn pipeline via the legend.
  const hero = findFeaturedPipeline(parcel, pipelines, carveOutSlug);
  if (hero?.tap_lng != null && hero?.tap_lat != null) {
    pins.push({ key: 'hero', mark: 'A', lng: Number(hero.tap_lng), lat: Number(hero.tap_lat), className: 'accent' });
  }
  {
    const seen = new Set<string>([hero?.operator || hero?.name || '']);
    const extraMarks = ['A′', 'A″'];
    let idx = 0;
    for (const p of pipelines) {
      if (idx >= extraMarks.length) break;
      // Skip gathering pipelines — they're local feeders, not BTM tap candidates.
      // Brookeland (Energy Transfer, 12.75″) on Paces is the prototype.
      if ((p as any).is_gathering === true) continue;
      const key = p.operator || p.name || '';
      if (!key || seen.has(key)) continue;
      seen.add(key);
      if (p.tap_lng == null || p.tap_lat == null) continue;
      pins.push({ key: `pipe-${idx}`, mark: extraMarks[idx], lng: Number(p.tap_lng), lat: Number(p.tap_lat), className: '' });
      idx++;
    }
  }

  // B — primary substation
  const sub = nearestFeatures?.substation;
  if (sub) pins.push({ key: 'sub', mark: 'B', lng: sub.lng, lat: sub.lat, className: 'square' });

  // C — on-site fiber: centered on the hex coverage grouping (falls back to parcel centroid).
  //     If no on-site fiber but a carrier backbone is nearby, place C at the nearest route vertex.
  //     Styling is neutral — we do not make an enterprise/"ok" claim on the memo; enterprise
  //     vs residential grading requires carrier inquiry and the stale FCC `fiber_has_enterprise`
  //     column can't reliably signal it (see feedback_v2_briefing_no_client_recompute).
  const hasFiber = parcel.fiber_provider_count != null && parcel.fiber_provider_count > 0;
  const hasCarrierFiberBackbone = (parcel.nearest_carrier_fiber_name && parcel.nearest_carrier_fiber_miles != null) ||
    (parcel.carrier_fiber_nearest && parcel.carrier_fiber_nearest.length > 0);
  if (hasFiber) {
    const hexCenter = nearbyPolygonCentroid(fiberCoverage, centroidLng, centroidLat);
    const [cLng, cLat] = hexCenter || [centroidLng, centroidLat];
    pins.push({ key: 'fiber', mark: 'C', lng: cLng, lat: cLat, className: '' });
  } else if (hasCarrierFiberBackbone) {
    const pt = nearestVertexInFC(carrierRoutes, centroidLng, centroidLat);
    if (pt) pins.push({ key: 'fiber', mark: 'C', lng: pt[0], lat: pt[1], className: '' });
  }

  // C′ — carrier fiber backbone touch point (shown alongside on-site C when both exist,
  //      typically Segra in WV). Placed at the nearest carrier route vertex.
  if (hasFiber && hasCarrierFiberBackbone) {
    const pt = nearestVertexInFC(carrierRoutes, centroidLng, centroidLat);
    if (pt) pins.push({ key: 'fiber_backbone', mark: 'C′', lng: pt[0], lat: pt[1], className: '' });
  }

  // D — railroad (suppress when API returned a far-away result, usually means table
  // has no coverage for this region and the KNN picked the nearest row in another state).
  // The pin is nudged outboard (away from the parcel, along the centroid→rail bearing)
  // so it doesn't land on top of pipelines running along the same corridor.
  const rail = nearestFeatures?.railroad;
  // Paces hotfix: BNSF runs along Hwy 96 just east of the parcel, co-located with the
  // FiberLight backbone. The west-slide overshoots and lands miles off the actual track
  // (the existing FRA segments to the NW are way west). Pin on the rail's true coords.
  const isPaces = parcel.id === 'a9ec56ff-f6dc-490d-9ae0-fabfa790081f';
  if (rail && rail.distance_miles != null && Number(rail.distance_miles) < 50) {
    if (isPaces) {
      // Nudge ~0.028° (~2.4 km) west so the D circle sits visibly on the FiberLight
      // dotted line rather than just east of it inside the parcel polygon.
      // Plus a small southward shift (~0.35 mi) so D doesn't stack on top of E
      // (Hwy 96), which BNSF runs along through this stretch.
      pins.push({ key: 'rail', mark: 'D', lng: rail.lng - 0.028, lat: rail.lat - 0.005, className: 'rail' });
    } else {
      // Slide the D pin west along the same rail line so it sits off stacked
      // pipelines but still visually ON the track. Falls back to a flat -0.07°
      // westward offset (same latitude) if we can't find a line vertex.
      const westPoint = westAlongNearestLine(railroads, rail.lng, rail.lat);
      pins.push({
        key: 'rail',
        mark: 'D',
        lng: westPoint ? westPoint[0] : rail.lng - 0.07,
        lat: westPoint ? westPoint[1] : rail.lat,
        className: 'rail',
      });
    }
  }

  // E — nearest highway. Identifies the road by parcel.nearest_highway_name and
  // places the pin at the closest vertex on the matching highway feature in
  // mapLayers.highways. Capped at 5 mi to stay consistent with the bbox fold-in.
  if (parcel.nearest_highway_name && parcel.nearest_highway_miles != null && Number(parcel.nearest_highway_miles) < 5) {
    const target = parcel.nearest_highway_name.toUpperCase();
    let best: [number, number] | null = null;
    let bestDist = Infinity;
    for (const f of highways?.features || []) {
      const featName = (f.properties?.name || '').toString().toUpperCase();
      if (!featName) continue;
      if (!featName.includes(target) && !target.includes(featName)) continue;
      const g = f.geometry;
      if (!g) continue;
      const rings: number[][][] = g.type === 'LineString' ? [g.coordinates as number[][]]
        : g.type === 'MultiLineString' ? (g.coordinates as number[][][])
        : [];
      for (const ring of rings) {
        for (const [lng, lat] of ring) {
          const d = (lng - centroidLng) ** 2 + (lat - centroidLat) ** 2;
          if (d < bestDist) { bestDist = d; best = [lng, lat]; }
        }
      }
    }
    if (best) {
      pins.push({ key: 'highway', mark: 'E', lng: best[0], lat: best[1], className: 'rail' });
    }
  }

  // F — on-corridor compressor station (was E; moved when E was reassigned to
  // highway). Within 2 mi of parcel centroid; otherwise hidden.
  const comp = nearestPointFeature(compressors, centroidLng, centroidLat);
  if (comp && comp.miles <= 2) {
    pins.push({ key: 'compressor', mark: 'F', lng: comp.lng, lat: comp.lat, className: 'square' });
  }

  // G — gas plant (only if on-corridor; beyond 10 mi is noise on BTM schematics)
  const gp = nearestFeatures?.gasPlant;
  if (gp && gp.distance_miles != null && gp.distance_miles < 10) {
    pins.push({ key: 'gas', mark: 'G', lng: gp.lng, lat: gp.lat, className: '' });
  }

  return pins;
}

function computeBounds(
  parcel: ParcelDetail,
  pins: PinDef[],
  carveOut?: OperatorCarveOut | null,
  highwaysFC?: GeoJSON.FeatureCollection,
  siteBandFC?: GeoJSON.FeatureCollection,
): mapboxgl.LngLatBoundsLike {
  const centroidLng = Number(parcel.lng);
  const centroidLat = Number(parcel.lat);

  let minLng = centroidLng;
  let maxLng = centroidLng;
  let minLat = centroidLat;
  let maxLat = centroidLat;

  const polygonCoords = (g: GeoJSON.Geometry | null): number[][] => {
    if (!g) return [];
    if (g.type === 'Polygon') return (g as GeoJSON.Polygon).coordinates[0];
    if (g.type === 'MultiPolygon') return (g as GeoJSON.MultiPolygon).coordinates.flat(2);
    return [];
  };

  for (const [lng, lat] of polygonCoords(parcel.geojson)) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  // Fold the carve-out polygon into the initial bounds so the schematic frames the
  // full assembly, not just the briefing parcel. Without this, the dashed perimeter
  // around the other 3 Paces Creek parcels would extend off-screen.
  for (const [lng, lat] of polygonCoords(carveOut?.geojson || null)) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }

  // Western band (Crown Pine A1 only — the server sends mapLayers.siteBand for
  // that carve-out and no other). It is part of the site, not an overlay, so it
  // frames like the parcel: without this the pipeline crossing sits off-canvas.
  for (const f of siteBandFC?.features || []) {
    for (const [lng, lat] of polygonCoords(f.geometry)) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
  }

  // Fold nearby highway vertices into the bbox so roads close to the parcel are
  // visible in the schematic frame (Paces Hwy 96 sits ~3 mi west of the rep parcel
  // centroid; without this it falls just outside the carve-out + pins bbox). Cap
  // at 5 mi so distant highways don't blow out the zoom on parcels that already
  // have a lot of context to fit.
  if (highwaysFC?.features?.length) {
    const HIGHWAY_RADIUS_MI = 5;
    const lonMiPerDeg = Math.cos((centroidLat * Math.PI) / 180) * 69;
    const radiusDegLng = HIGHWAY_RADIUS_MI / lonMiPerDeg;
    const radiusDegLat = HIGHWAY_RADIUS_MI / 69;
    for (const feat of highwaysFC.features) {
      const g = feat.geometry;
      if (!g) continue;
      const rings: number[][][] = g.type === 'LineString'
        ? [g.coordinates as number[][]]
        : g.type === 'MultiLineString'
          ? (g.coordinates as number[][][])
          : [];
      for (const ring of rings) {
        for (const [lng, lat] of ring) {
          if (Math.abs(lng - centroidLng) > radiusDegLng) continue;
          if (Math.abs(lat - centroidLat) > radiusDegLat) continue;
          if (lng < minLng) minLng = lng;
          if (lng > maxLng) maxLng = lng;
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
        }
      }
    }
  }

  for (const pin of pins) {
    const dLng = Math.abs(pin.lng - centroidLng);
    const dLat = Math.abs(pin.lat - centroidLat);
    const approxMiles = Math.sqrt(dLng * dLng + dLat * dLat) * 69;
    if (approxMiles > 10) continue;
    if (pin.lng < minLng) minLng = pin.lng;
    if (pin.lng > maxLng) maxLng = pin.lng;
    if (pin.lat < minLat) minLat = pin.lat;
    if (pin.lat > maxLat) maxLat = pin.lat;
  }

  return [[minLng, minLat], [maxLng, maxLat]];
}

export const SchematicMap: React.FC<SchematicMapProps> = ({ parcel, pipelines, mapLayers, nearestFeatures, carveOut, carveOutSlug }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [exploring, setExploring] = useState(false);

  const pins = useMemo(
    () => buildPins(parcel, pipelines, nearestFeatures, mapLayers.carrierRoutes, mapLayers.fiberCoverage, mapLayers.compressors, mapLayers.railroads, mapLayers.highways, carveOutSlug),
    [parcel, pipelines, nearestFeatures, mapLayers.carrierRoutes, mapLayers.fiberCoverage, mapLayers.compressors, mapLayers.railroads, mapLayers.highways, carveOutSlug],
  );
  const bounds = useMemo(() => computeBounds(parcel, pins, carveOut, mapLayers.highways, mapLayers.siteBand), [parcel, pins, carveOut, mapLayers.highways, mapLayers.siteBand]);

  const parcelFC = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!parcel.geojson) return EMPTY_FC;
    return { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: parcel.geojson, properties: {} }] };
  }, [parcel.geojson]);

  const carveOutFC = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!carveOut?.geojson) return EMPTY_FC;
    return { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: carveOut.geojson, properties: { id: carveOut.id, name: carveOut.name } }] };
  }, [carveOut?.geojson, carveOut?.id, carveOut?.name]);

  useEffect(() => {
    if (!containerRef.current) return;
    mapboxgl.accessToken = getMapboxToken();

    const m = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      bounds: bounds,
      fitBoundsOptions: { padding: 50 },
      preserveDrawingBuffer: true,
      attributionControl: false,
      scrollZoom: false,
      dragPan: false,
      dragRotate: false,
      doubleClickZoom: false,
      touchZoomRotate: false,
      keyboard: false,
      pitchWithRotate: false,
    });

    m.on('load', () => {
      // --- Layers ported from BriefingMap.tsx, colors adapted for cream palette ---

      // Parcel boundary
      m.addSource('parcel', { type: 'geojson', data: parcelFC });
      m.addLayer({ id: 'parcel-fill', source: 'parcel', type: 'fill', paint: { 'fill-color': '#B5472B', 'fill-opacity': 0.08 } } as any);
      m.addLayer({ id: 'parcel-outline', source: 'parcel', type: 'line', paint: { 'line-color': '#B5472B', 'line-width': 2 } } as any);

      // Operator carve-out (multi-parcel single-owner assembly e.g. Paces Creek
      // Woodlands' 4 parcels). Sunrise dashed perimeter so the IC reader can see
      // the proposed assembly extends beyond the briefing parcel. Inserted with
      // beforeId='parcel-fill' so the briefing parcel still reads as the focus
      // (parcel layers cover the carve-out where they overlap; the dashed outline
      // shows in the other member parcels' areas). Mirrors v1 BriefingMap.tsx:620-647.
      if (carveOut?.geojson) {
        m.addSource('carveout', { type: 'geojson', data: carveOutFC });
        m.addLayer({ id: 'carveout-fill', source: 'carveout', type: 'fill', paint: { 'fill-color': '#C4705A', 'fill-opacity': 0.12 } } as any, 'parcel-fill');
        m.addLayer({ id: 'carveout-outline', source: 'carveout', type: 'line', paint: { 'line-color': '#C4705A', 'line-width': 2, 'line-dasharray': [3, 2], 'line-opacity': 0.85 } } as any, 'parcel-fill');
      }

      // Western band — part of the site outline, not an overlay. Served only for
      // the Crown Pine A1 carve-out (api-server/routes/briefing.js), so this is
      // inert on every other briefing. Same accent as the parcel with a dashed
      // edge so the core/band seam stays readable, and the pipeline layers below
      // draw ON TOP so the crossing is visible where it actually happens.
      if (mapLayers.siteBand?.features?.length) {
        m.addSource('siteband', { type: 'geojson', data: mapLayers.siteBand });
        m.addLayer({ id: 'siteband-fill', source: 'siteband', type: 'fill', paint: { 'fill-color': '#B5472B', 'fill-opacity': 0.08 } } as any);
        m.addLayer({ id: 'siteband-outline', source: 'siteband', type: 'line', paint: { 'line-color': '#B5472B', 'line-width': 1.6, 'line-dasharray': [4, 2], 'line-opacity': 0.9 } } as any);
        m.addLayer({ id: 'siteband-label', source: 'siteband', type: 'symbol', layout: { 'text-field': ['get', 'label'], 'text-size': 10, 'text-max-width': 10 }, paint: { 'text-color': '#17140E', 'text-halo-color': '#F2EDE1', 'text-halo-width': 1.6 } } as any);
      }

      // Pipelines — all types, always shown (BriefingMap lines 518-527).
      // Operator identification happens via A / A′ / A″ pins + legend rows, not
      // text-on-line. Keeps the schematic typographically silent like the rest.
      addGroup(m, 'pipelines-gas', mapLayers.pipelinesGas, [
        { id: 'pipelines-gas', type: 'line', paint: { 'line-color': '#3E4E5C', 'line-width': 2 } },
      ]);
      addGroup(m, 'pipelines-dsw', mapLayers.pipelinesDesertSw, [
        { id: 'pipelines-dsw-glow', type: 'line', paint: { 'line-color': '#B5472B', 'line-width': 12, 'line-opacity': 0.2, 'line-blur': 6 } },
        { id: 'pipelines-dsw', type: 'line', paint: { 'line-color': '#B5472B', 'line-width': 4 } },
      ]);
      addGroup(m, 'pipelines-gathering', mapLayers.pipelinesGathering, [
        { id: 'pipelines-gathering', type: 'line', paint: { 'line-color': '#8A9CAA', 'line-width': 1.5, 'line-opacity': 0.6 } },
      ]);

      // Substations (BriefingMap line 528-529)
      addGroup(m, 'substations', mapLayers.substations, [
        { id: 'substations', type: 'circle', paint: { 'circle-color': '#17140E', 'circle-radius': 4, 'circle-stroke-color': '#F2EDE1', 'circle-stroke-width': 1.5 } },
      ]);

      // Fiber coverage hexes (BriefingMap lines 531-533)
      addGroup(m, 'fiber-coverage', mapLayers.fiberCoverage, [
        { id: 'fiber-fill', type: 'fill', paint: { 'fill-color': '#506B2E', 'fill-opacity': 0.15 } },
        { id: 'fiber-outline', type: 'line', paint: { 'line-color': '#506B2E', 'line-width': 1, 'line-opacity': 0.4 } },
      ]);

      // Carrier fiber backbone routes (BriefingMap lines 566-568)
      addGroup(m, 'carrier-fiber', mapLayers.carrierRoutes, [
        { id: 'carrier-fiber', type: 'line', paint: { 'line-color': '#506B2E', 'line-width': 2.5, 'line-opacity': 0.9, 'line-dasharray': [6, 3] } },
      ]);

      // Highways — bumped to v1's active-state styling (#8A9CAA / 2 / 0.7) so
      // the road network is legible on the cream paper rather than a barely-there
      // wash. Matches the BriefingMap.tsx active highlight paint at line ~260.
      addGroup(m, 'highways', mapLayers.highways, [
        { id: 'highways', type: 'line', paint: { 'line-color': '#8A9CAA', 'line-width': 2, 'line-opacity': 0.7 } },
      ]);

      // Railroads (BriefingMap lines 561-563)
      addGroup(m, 'railroads', mapLayers.railroads, [
        { id: 'railroads', type: 'line', paint: { 'line-color': '#A8AFB5', 'line-width': 2, 'line-dasharray': [6, 4] } },
      ]);

      // Gas plants (BriefingMap lines 558-560)
      addGroup(m, 'gas-plants', mapLayers.gasPlants, [
        { id: 'gas-plants', type: 'circle', paint: { 'circle-color': '#B5472B', 'circle-radius': 5, 'circle-stroke-color': '#F2EDE1', 'circle-stroke-width': 1.5 } },
      ]);

      // Airports (BriefingMap lines 587-589)
      addGroup(m, 'airports', mapLayers.airports, [
        { id: 'airports', type: 'circle', paint: { 'circle-color': '#6A7E8E', 'circle-radius': 5, 'circle-stroke-color': '#F2EDE1', 'circle-stroke-width': 1.5 } },
      ]);

      // Hover popup
      const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: 'v2-map-popup', offset: 12 });
      for (const layerId of ['pipelines-gas', 'pipelines-dsw', 'carrier-fiber', 'substations', 'gas-plants']) {
        if (!m.getLayer(layerId)) continue;
        m.on('mouseenter', layerId, (e) => {
          m.getCanvas().style.cursor = 'pointer';
          const f = e.features?.[0];
          if (!f) return;
          const props = f.properties || {};
          const name = props.operator || props.name || props.NAME || props.carrier || '';
          const dist = props.distance_miles != null ? `${Number(props.distance_miles).toFixed(1)} mi` : '';
          const diameter = props.diameter_inches ? `${props.diameter_inches}"` : '';
          const parts = [name, diameter, dist].filter(Boolean);
          if (parts.length) popup.setLngLat(e.lngLat).setHTML(`<span>${parts.join(' · ')}</span>`).addTo(m);
        });
        m.on('mouseleave', layerId, () => {
          m.getCanvas().style.cursor = '';
          popup.remove();
        });
      }

      // Place pin markers
      const markers: mapboxgl.Marker[] = [];
      for (const pin of pins) {
        const el = document.createElement('div');
        el.className = `v2-map-pin ${pin.className}`.trim();
        el.textContent = pin.mark;
        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([pin.lng, pin.lat])
          .addTo(m);
        markers.push(marker);
      }
      markersRef.current = markers;
    });

    mapRef.current = m;
    return () => {
      markersRef.current.forEach(mk => mk.remove());
      markersRef.current = [];
      m.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleExplore = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    if (exploring) {
      m.scrollZoom.disable();
      m.dragPan.disable();
      m.fitBounds(bounds, { padding: 50, duration: 600 });
      setExploring(false);
    } else {
      m.scrollZoom.enable();
      m.dragPan.enable();
      setExploring(true);
    }
  }, [exploring, bounds]);

  return (
    <div className="v2-schematic-map">
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <button className="v2-map-explore" onClick={toggleExplore} type="button">
        {exploring ? 'Reset view' : 'Explore'}
      </button>
    </div>
  );
};

function addGroup(
  m: mapboxgl.Map,
  sourceId: string,
  data: GeoJSON.FeatureCollection | undefined,
  layers: Array<{ id: string; type: string; paint: Record<string, unknown> }>,
) {
  m.addSource(sourceId, { type: 'geojson', data: data || EMPTY_FC });
  for (const layer of layers) {
    m.addLayer({ source: sourceId, ...layer } as any);
  }
}
